// Thin API-Client für das lokale Backend.
// Antworten sind bewusst klein gehalten, Backend macht keine Geschäftslogik
// für Auth — das erledigt Supabase direkt im Client.

import { getDb } from './storage/db';
import type { CruxPoint, ShuttleArea } from '@adtu/shared';
import { REGIONS, REGION_IDS } from '@adtu/shared';
import { setDynamicRegions } from './regionCheck';

export interface TilesEntry {
  filename: string;
  url: string;
  sizeBytes: number;
  updatedAt: string;
}

/**
 * Pro Region ein Eintrag mit allen 3 PMTiles-Files. id matched zum
 * Filename-Prefix (siehe scripts/generate-pmtiles.sh) und zur shared
 * `RegionId`-Konstante.
 */
export interface RegionManifestEntry {
  id: string;
  humanName: string;
  bounds: { west: number; south: number; east: number; north: number };
  center: [number, number];
  map: TilesEntry | null;
  contours: TilesEntry | null;
  hillshade: TilesEntry | null;
}

export interface TilesManifest {
  // Backwards-compat: zeigt auf die Liguria-Files (oder per
  // PMTILES_*_FILE-Env konfigurierte). Alte Clients lesen das.
  map: TilesEntry | null;
  contours: TilesEntry | null;
  hillshade: TilesEntry | null;
  // Wave 14cf: Multi-Region. Neue Clients iterieren regions[] und ignorieren
  // die Top-Level-Slots. Bei alten Backends ist das Feld optional/leer —
  // dann fallen wir auf den Liguria-only-Pfad zurück.
  regions?: RegionManifestEntry[];
}

const MANIFEST_CACHE_KEY = 'tiles-manifest-last-good';
// Muss mit offline.ts und vite.config.ts runtimeCaching.cacheName matchen.
const PMTILES_CACHE_NAME = 'pmtiles-v2';
// NetworkFirst-Timeout: wenn Backend in 3.5s nicht antwortet, nehmen wir den
// Cache. Kein Hängenbleiben auf schwachem Netz mehr.
const MANIFEST_NET_TIMEOUT_MS = 3500;

async function readManifestCache(): Promise<TilesManifest | null> {
  try {
    const db = await getDb();
    const cached = (await db.get('settings', MANIFEST_CACHE_KEY)) as
      | TilesManifest
      | undefined;
    return cached ?? null;
  } catch (err) {
    console.warn('[api] manifest-cache read failed', err);
    return null;
  }
}

async function writeManifestCache(m: TilesManifest): Promise<void> {
  try {
    const db = await getDb();
    await db.put('settings', m, MANIFEST_CACHE_KEY);
  } catch (err) {
    console.warn('[api] manifest-cache write failed', err);
  }
}

/**
 * Last-resort-Fallback: scannt die PMTiles-CacheStorage direkt und rekonstruiert
 * ein Manifest aus dem was tatsächlich offline verfügbar ist. Das ist der
 * robusteste Weg — er funktioniert auch wenn IndexedDB leer ist (z.B. weil der
 * User die PMTiles mit einem älteren Build gecacht hat, bevor es den
 * IndexedDB-Cache gab).
 *
 * URL-Pattern: /tiles/<filename>.pmtiles
 * Kategorisierung nach Dateiname:
 *   - enthält "contour"    → contours
 *   - enthält "hillshade"  → hillshade
 *   - sonst                → map
 */
async function readManifestFromCacheStorage(): Promise<TilesManifest | null> {
  try {
    if (typeof caches === 'undefined') return null;
    const cache = await caches.open(PMTILES_CACHE_NAME);
    const keys = await cache.keys();
    if (keys.length === 0) return null;

    // Wave 14cf-fix8: Wir rekonstruieren JETZT auch `regions[]` aus den
    // Dateinamen. Vorher landete alles undifferenziert in den Top-Level-
    // Slots, was die OfflinePage in den Single-Region-Fallback zwang
    // (mit Hardcoded "liguria" als ID — selbst wenn nur Elba gecached
    // war). Filename-Pattern: `<region>-<type>.pmtiles`, z.B.
    // `elba-map.pmtiles`, `liguria-contours.pmtiles`.
    type RegionAcc = {
      id: string;
      humanName: string;
      bounds: { west: number; south: number; east: number; north: number } | null;
      center: [number, number] | null;
      map: { filename: string; url: string; sizeBytes: number; updatedAt: string } | null;
      contours: { filename: string; url: string; sizeBytes: number; updatedAt: string } | null;
      hillshade: { filename: string; url: string; sizeBytes: number; updatedAt: string } | null;
    };
    const regionMap = new Map<string, RegionAcc>();

    const result: TilesManifest = { map: null, contours: null, hillshade: null };
    for (const req of keys) {
      const url = new URL(req.url);
      if (!/\/tiles\/.+\.pmtiles$/i.test(url.pathname)) continue;

      const filename = url.pathname.split('/').pop() || '';
      const lower = filename.toLowerCase();
      let key: 'map' | 'contours' | 'hillshade' = 'map';
      if (lower.includes('contour')) key = 'contours';
      else if (lower.includes('hillshade')) key = 'hillshade';

      // Region aus Dateinamen extrahieren — zuerst bekannte IDs prüfen.
      // Fallback: "unknown" wenn nichts matched.
      let regionId: string = 'unknown';
      let regionHuman = 'Unknown';
      let regionBounds:
        | { west: number; south: number; east: number; north: number }
        | null = null;
      let regionCenter: [number, number] | null = null;
      // Wave 14ed: REGION_IDS ist normalerweise leer (keine eingebaute
      // Region mehr) — die Schleife laeuft dann gar nicht. Der Lookup ist
      // trotzdem optional-chained, weil REGIONS jetzt Record<string, …> ist.
      for (const knownId of REGION_IDS) {
        if (lower.startsWith(`${knownId}-`) || lower.startsWith(`${knownId}_`)) {
          const info = REGIONS[knownId];
          if (!info) break;
          regionId = knownId;
          regionHuman = info.humanName;
          regionBounds = info.bounds;
          regionCenter = info.center;
          break;
        }
      }

      let sizeBytes = 0;
      try {
        const resp = await cache.match(req, { ignoreVary: true });
        if (resp) {
          const cl = Number(resp.headers.get('Content-Length') || '0');
          if (cl > 0) sizeBytes = cl;
          else {
            const blob = await resp.clone().blob();
            sizeBytes = blob.size;
          }
        }
      } catch {
        /* Grösse unbekannt — Anzeige zeigt dann 0 B */
      }

      const relUrl = url.pathname + url.search;
      const entry = {
        filename,
        url: relUrl,
        sizeBytes,
        updatedAt: new Date(0).toISOString(),
      };

      // Region-Akkumulator füttern.
      let acc = regionMap.get(regionId);
      if (!acc) {
        acc = {
          id: regionId,
          humanName: regionHuman,
          bounds: regionBounds,
          center: regionCenter,
          map: null,
          contours: null,
          hillshade: null,
        };
        regionMap.set(regionId, acc);
      }
      if (!acc[key]) acc[key] = entry;

      // Legacy-Slot — erste-Datei-gewinnt für Backwards-Compat.
      if (!result[key]) result[key] = entry;
    }

    if (regionMap.size > 0) {
      result.regions = Array.from(regionMap.values()).map((acc) => ({
        id: acc.id,
        humanName: acc.humanName,
        bounds: acc.bounds ?? { west: 0, south: 0, east: 0, north: 0 },
        center: acc.center ?? [0, 0],
        map: acc.map,
        contours: acc.contours,
        hillshade: acc.hillshade,
      }));
    }

    if (!result.map && !result.contours && !result.hillshade && (!result.regions || result.regions.length === 0)) {
      return null;
    }
    return result;
  } catch (err) {
    console.warn('[api] manifest scan from CacheStorage failed', err);
    return null;
  }
}

/**
 * Wave 14ba: Manifest-Fetch ist jetzt LOCAL-FIRST.
 *
 * Vorher: fetch mit 3.5s Timeout → erst bei Fehler auf IDB-Cache fallen.
 * Nach Refresh hat die App also bis zu 3.5 Sekunden gewartet, obwohl die
 * Daten lokal längst da waren. Bremsklotz für "offline besser als online".
 *
 * Jetzt:
 *  1. Wenn IDB-Cache ein plausibles Manifest hat → sofort zurückgeben.
 *  2. Parallel (non-blocking) einen Stale-While-Revalidate-Fetch starten,
 *     der bei Erfolg den IDB-Cache für den nächsten Start aktualisiert.
 *     Der aktuelle Aufruf wartet NICHT auf das Ergebnis.
 *  3. Wenn IDB leer ist (erster Start), fallen wir auf CacheStorage-Scan
 *     zurück — dort liegen die .pmtiles-Dateien und wir können ein
 *     Manifest rekonstruieren.
 *  4. Wenn auch das leer ist, schlussendlicher Netz-Fetch (einmaliger,
 *     blockender Versuch). Sollte nur beim allerersten Kaltstart mit Netz
 *     aber ohne Gate-Vorlauf passieren.
 *
 * Wichtig: diese Funktion wirft NIE — ein Fehler hier würde die ganze App
 * crashen (Flicker-Ursache #1 bei schwachem Netz).
 */
// Wave 14ec: der eigentliche Manifest-Loader heisst jetzt tilesManifestInner.
// Der exportierte Wrapper darunter registriert zusaetzlich die Region-Bounds
// (inkl. selbst gebauter Korridore) in lib/regionCheck — ohne das kennt die
// Punkt-in-Region-Pruefung nur '3pbr' und sperrt den User in seinem eigenen
// Korridor aus (siehe Kommentarblock in regionCheck.ts).
async function tilesManifestInner(opts?: { forceNetwork?: boolean }): Promise<TilesManifest> {
  // Wave 14cf-fix8: Cache-Hit nur dann short-circuiten wenn der Cache
  // VOLLSTÄNDIG ist — also `regions[]` enthält. Vorher hat der
  // Cache-Hit auch dann gegriffen wenn nur Legacy-Slots da waren
  // (map/contours/hillshade); das passiert wenn der CacheStorage-Scan-
  // Fallback einmal die IDB überschrieben hat (siehe
  // readManifestFromCacheStorage, das `regions[]` nicht rekonstruiert).
  // Folge: User sah in der OfflinePage nur eine Region (immer als
  // "Liguria" gerendert wegen des Hardcoded-Fallbacks dort), kein Weg
  // die zweite Region nachzuziehen.
  // Jetzt: stripped IDB-Cache fällt durch zu fresh-Network-Fetch wenn
  // online; wenn offline, immer noch zur stripped Version zurückfallen
  // (besser als nix).
  const cached = await readManifestCache();
  const isCompleteCached =
    cached && cached.regions && cached.regions.length > 0;
  // forceNetwork (Offline-Seite): immer frisch holen, damit neu gebaute
  // Regionen (z.B. HERO) sofort erscheinen statt erst nach Hintergrund-
  // Revalidierung + zweitem Öffnen. Cache bleibt Offline-Fallback unten.
  if (isCompleteCached && !opts?.forceNetwork) {
    void revalidateManifestInBackground();
    console.info('[api] tilesManifest: local-first hit (IDB, complete)');
    return cached;
  }

  // --- Ebene 2: Netz-Fetch (bevorzugt wenn IDB stripped ist) ---
  // Wenn online + Server liefert komplettes Manifest mit regions[], das
  // nehmen — auch wenn der CacheStorage-Scan ein paar Files findet.
  const fresh = await fetchManifestFromNetwork();
  if (fresh && fresh.regions && fresh.regions.length > 0) {
    await writeManifestCache(fresh);
    console.info('[api] tilesManifest: fresh from network (regions[])');
    return fresh;
  }
  // forceNetwork + offline/Netz-Fehler: vollständigen Cache als Fallback nehmen.
  if (isCompleteCached) {
    console.info('[api] tilesManifest: forceNetwork failed → complete IDB cache');
    return cached;
  }

  // --- Ebene 3: stripped Caches als Fallback ---
  // Reihenfolge: zuerst IDB-stripped (falls vorhanden), dann
  // CacheStorage-Scan. WICHTIG: stripped Versionen NICHT als
  // "complete" in IDB persistieren — sonst überschreibt das einen
  // späteren network-fetch nicht. Wir lesen sie nur als Display-
  // Fallback, schreiben sie nicht.
  if (cached && (cached.map || cached.contours || cached.hillshade)) {
    void revalidateManifestInBackground();
    console.info('[api] tilesManifest: stripped IDB fallback (no regions[])');
    return cached;
  }

  const fromCache = await readManifestFromCacheStorage();
  if (fromCache) {
    console.info('[api] tilesManifest: CacheStorage scan fallback');
    // KEIN writeManifestCache — sonst klemmt sich diese stripped Version
    // in die IDB und blockt zukünftige fresh-Fetches (alter fix6-Bug).
    void revalidateManifestInBackground();
    return fromCache;
  }

  // Letzte Chance: legacy fresh fallback wenn fresh nur top-level slots hatte.
  if (fresh && (fresh.map || fresh.contours || fresh.hillshade)) {
    await writeManifestCache(fresh);
    return fresh;
  }

  console.warn('[api] tilesManifest: nichts lokal, nichts online → leer');
  return { map: null, contours: null, hillshade: null };
}

/** Separater Netz-Fetch mit Timeout — nur noch in Fallback-Pfad + SWR. */
async function fetchManifestFromNetwork(): Promise<TilesManifest | null> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), MANIFEST_NET_TIMEOUT_MS);
  try {
    const res = await fetch('/api/tiles/manifest', { signal: ctrl.signal });
    if (!res.ok) throw new Error(String(res.status));
    return (await res.json()) as TilesManifest;
  } catch (err) {
    console.info('[api] manifest network fetch failed (expected offline):', err);
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Stale-While-Revalidate: feuert im Hintergrund einen Netz-Request und
 * aktualisiert den IDB-Cache bei Erfolg. Kein await, kein Blockieren.
 * Globale Dedupe: wenn schon ein Revalidate läuft, kein zweiter.
 */
let revalidateInFlight = false;
async function revalidateManifestInBackground(): Promise<void> {
  if (revalidateInFlight) return;
  revalidateInFlight = true;
  try {
    const fresh = await fetchManifestFromNetwork();
    if (fresh && (fresh.map || fresh.contours || fresh.hillshade)) {
      await writeManifestCache(fresh);
      console.info('[api] manifest SWR: IDB-Cache aktualisiert');
    }
  } finally {
    revalidateInFlight = false;
  }
}

export interface RemoteTrail {
  filename: string;
  name?: string;
  trailType?: string;
  difficulty?: string;
  colorHex?: string | null;
  description?: string | null;
  photos?: string[];
  /**
   * Wave 14cf: Region-Tag — 'liguria' | 'elba' | null (unklassifiziert).
   * Backend resolved entweder aus meta.region (explizit gesetzt) oder
   * via Auto-Classify aus den GPX-Bounds.
   */
  region?: 'liguria' | 'elba' | null;
  /**
   * Schlüsselstellen — Admin-annotierte Gefahr-Punkte entlang des Trails.
   * Das Backend liefert IMMER ein Array (leer wenn der Admin nichts gesetzt
   * hat); Legacy-Trails ohne das Feld in der JSON werden im Backend auf []
   * gemapped. Optional in diesem Type, weil ältere gecachte Antworten
   * (IndexedDB-Manifest-Cache) das Feld noch nicht haben könnten.
   */
  cruxPoints?: CruxPoint[];
  /**
   * Wave 14bl: Backend-seitig berechnete Höhen-Samples (opentopodata/eudem25m,
   * geglättet) ODER von einem früheren Rider-Client hochgepushte. Immer 100
   * Samples, gleichmäßig entlang Track-Länge. Der Client spiegelt sie in den
   * IndexedDB-`trailElevations`-Store, damit das Höhenprofil beim allerersten
   * Trail-Besuch sofort — und dauerhaft offline — da ist.
   * null wenn noch gar nichts gesampled wurde (sehr frischer Upload ohne
   * Netz beim Admin-Upload-Moment, oder pre-Wave-14bl Trail ohne Push).
   */
  elevationSamples?: number[] | null;
  /** Smoothing-Version des Samples-Arrays; muss zur Client-Konstante passen. */
  elevationSmoothingVersion?: number | null;
  /** Track-Länge zum Sampling-Zeitpunkt — für Cache-Validierung beim Rider. */
  elevationTrackLengthMeters?: number | null;
  sizeBytes: number;
  updatedAt: string;
}

export interface TrailListing {
  trails: RemoteTrail[];
}

// Harter Timeout für Trail-Requests — ohne das hängt die App bei
// flakeligem WLAN manchmal 30+ Sekunden und React-Effects feuern mehrfach,
// was zu sichtbarem Flackern führt. 5s ist grosszügig für kleine GPX-Payloads
// und klein genug um den User nicht warten zu lassen.
const TRAILS_NET_TIMEOUT_MS = 5000;

async function fetchWithTimeout(url: string, timeoutMs: number): Promise<Response> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    return await fetch(url, { signal: ctrl.signal });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Lädt die Trail-Liste vom Backend.
 *
 * Wave 14cf: optional `region` einschränkbar — nur Trails dieser Region
 * werden zurückgegeben. Ohne Param liefert der Backend ALLE Trails inkl.
 * Region-Tag, dann kann der Caller selber filtern (so machen's
 * downstream-Konsumenten wie der Region-Switch im OfflinePage, die
 * sich gleichzeitig ALLE Regionen anzeigen wollen).
 */
export async function listTrails(opts?: {
  region?: 'liguria' | 'elba';
}): Promise<TrailListing> {
  const url = opts?.region
    ? `/api/trails?region=${encodeURIComponent(opts.region)}`
    : '/api/trails';
  const res = await fetchWithTimeout(url, TRAILS_NET_TIMEOUT_MS);
  if (!res.ok) throw new Error(`listTrails ${res.status}`);
  return res.json();
}

export async function fetchTrailGpx(filename: string): Promise<string> {
  const res = await fetchWithTimeout(
    `/api/trails/${encodeURIComponent(filename)}`,
    TRAILS_NET_TIMEOUT_MS,
  );
  if (!res.ok) throw new Error(`fetchTrailGpx ${res.status}`);
  return res.text();
}

// --- DH Runs ---
// Ein Run ist eine geordnete Kette von Trails mit EIGENER GPX (die ganze
// Bergab-Fahrt als zusammenhängender Track). Die Rider-PWA listet sie unter
// /runs, und das Detail rendert Höhenprofil + Chain + Beschreibung.

export interface RemoteRun {
  id: string;
  name: string;
  trailIds: string[];
  gpxFilename: string;
  description?: string | null;
  difficulty: string;
  /** Wave 14dt: optional Shuttle-Area-Zuordnung. Vom Backend gefüllt aus
   *  data/runs/*.json (siehe routes/runs.js buildRunDto). 2-Char-Uppercase
   *  oder null wenn nicht zugewiesen. */
  shuttleAreaCode?: string | null;
  sizeBytes?: number;
  updatedAt: string;
}

export interface RunListing {
  runs: RemoteRun[];
}

export async function listRuns(): Promise<RunListing> {
  const res = await fetchWithTimeout('/api/runs', TRAILS_NET_TIMEOUT_MS);
  if (!res.ok) throw new Error(`listRuns ${res.status}`);
  return res.json();
}

export async function fetchRunGpx(filename: string): Promise<string> {
  const res = await fetchWithTimeout(
    `/api/runs/${encodeURIComponent(filename)}`,
    TRAILS_NET_TIMEOUT_MS,
  );
  if (!res.ok) throw new Error(`fetchRunGpx ${res.status}`);
  return res.text();
}

/**
 * Wave 14bl: Self-healing Elevation-Push.
 *
 * Der Client hat gerade lokal ein Höhenprofil berechnet (via MapLibre-DEM
 * ODER HTTP-opentopodata-Fallback) und speichert es in seinen IDB-Cache.
 * Parallel pushen wir es zum Backend, damit der NÄCHSTE User auf demselben
 * Trail es sofort mit der Trail-Liste mitbekommt und nicht erneut rechnen
 * muss.
 *
 * Wave 14bq: Der alte Fire-and-Forget-Pfad verlor auf flakeligen Handy-
 * Netzen Pushes komplett — der User sah das Profil lokal, aber ein anderes
 * Gerät musste es neu rechnen. Jetzt geht die Semantik durch die
 * elevationQueue (IDB-Store). `pushTrailElevationSamples()` ist das alte
 * Fire-and-Forget-Interface (nie-wirft), das jetzt intern via Queue läuft.
 * `rawPushElevationSamples()` ist der untergeschobene HTTP-Call, der bei
 * Netz-/HTTP-Fehler WIRFT — der Queue-Helper nutzt das um zwischen "push
 * erledigt" und "push pending" zu unterscheiden.
 *
 * Kleiner Timeout (4s) damit der Push nicht auf einem hängenden Server
 * Haken bleibt — das ist "nice to have", nicht critical path.
 */
export interface RawElevationPushResult {
  /** Server hat die Samples persistiert (echter Store-Write). */
  stored: boolean;
  /**
   * Server hat den Push akzeptiert aber NICHT übernommen (z. B. weil schon
   * server-sampled Samples existieren). Das ist KEIN Fehler — der Eintrag
   * darf aus der Queue raus, es gibt nichts zu retry'n.
   */
  skipped: boolean;
  /** Server-gelieferter Grund bei skipped=true (z. B. 'server_sample_exists'). */
  reason?: string;
}

/**
 * Low-Level-Push: wirft bei Netz-/HTTP-Fehler damit die Queue zwischen
 * "synced" und "pending" unterscheiden kann. Analog zu `rawPatchTrail` in
 * admin.ts. 4s-Timeout identisch zur alten Fire-and-Forget-Version.
 */
export async function rawPushElevationSamples(
  trailId: string,
  samples: number[],
  smoothingVersion: number,
  trackLengthMeters: number,
): Promise<RawElevationPushResult> {
  if (!trailId || !/^[a-zA-Z0-9._-]+$/.test(trailId)) {
    throw new Error('invalid_trail_id');
  }
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 4000);
  try {
    const res = await fetch(
      `/api/trails/${encodeURIComponent(trailId)}/elevation-samples`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ samples, smoothingVersion, trackLengthMeters }),
        signal: ctrl.signal,
      },
    );
    if (!res.ok) {
      // 400 'smoothing_version_mismatch' ist keine Retry-Chance (unser Client
      // müsste sich zuerst auf neuen Algorithmus updaten). 404 'trail_not_found'
      // auch nicht (der Trail existiert serverseitig gar nicht). Beide werfen
      // wir als harten Fehler mit einer Message die der Queue-Helper matchen
      // kann, um solche Queue-Einträge rauszuwerfen statt endlos zu retry'n.
      const text = await res.text().catch(() => '');
      throw new Error(`pushElevation ${res.status}: ${text}`);
    }
    const body = (await res.json().catch(() => null)) as {
      ok?: boolean;
      stored?: boolean;
      skipped?: boolean;
      reason?: string;
    } | null;
    return {
      stored: Boolean(body?.stored),
      skipped: Boolean(body?.skipped),
      reason: body?.reason,
    };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Altes Fire-and-Forget-Interface für Rückwärtskompatibilität. Wave 14bq
 * Callsites sollten bevorzugt `enqueueElevationPush()` nutzen — damit geht
 * der Push bei Netz-Flakes nicht verloren.
 */
export async function pushTrailElevationSamples(
  trailId: string,
  samples: number[],
  smoothingVersion: number,
  trackLengthMeters: number,
): Promise<void> {
  try {
    const res = await rawPushElevationSamples(
      trailId,
      samples,
      smoothingVersion,
      trackLengthMeters,
    );
    if (res.stored) {
      console.info(`[api] elevation push stored: ${trailId}`);
    } else if (res.skipped) {
      console.info(`[api] elevation push skipped (${res.reason}): ${trailId}`);
    }
  } catch (err) {
    console.info(`[api] pushTrailElevationSamples ${trailId} failed (expected offline):`, err);
  }
}

// --- Shuttle Areas (Wave 14dt) ---
// Holt die Liste aller Areas (PL/KA/IS/ME/BN initial, Admin-editierbar).
// Public-Endpoint — auth-frei. Fail-soft: bei Netz-Fehler leere Liste,
// das UI rendert dann einfach keine Badges.
export async function listShuttleAreas(): Promise<ShuttleArea[]> {
  try {
    const res = await fetch('/api/shuttle-areas', { credentials: 'include' });
    if (!res.ok) return [];
    const data = (await res.json()) as { areas: ShuttleArea[] };
    return Array.isArray(data.areas) ? data.areas : [];
  } catch (err) {
    console.warn('[api] listShuttleAreas failed', err);
    return [];
  }
}


/**
 * Oeffentlicher Einstieg. Identisch zu tilesManifestInner, meldet aber jedes
 * geladene Manifest an regionCheck weiter — egal ob es aus dem Netz, aus der
 * IDB oder aus dem CacheStorage-Scan kam. Fehler hier duerfen den Aufrufer
 * NIE treffen: das Manifest ist wichtiger als der Bounds-Cache.
 */
export async function tilesManifest(opts?: {
  forceNetwork?: boolean;
}): Promise<TilesManifest> {
  const manifest = await tilesManifestInner(opts);
  try {
    setDynamicRegions(
      (manifest.regions ?? []).map((r) => ({
        id: r.id,
        humanName: r.humanName,
        bounds: r.bounds,
      })),
    );
  } catch (err) {
    console.warn('[api] setDynamicRegions failed', err);
  }
  return manifest;
}
